import { CdkStepper } from '@angular/cdk/stepper';
import {
  AfterContentInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

import { Step } from './stepper.model';

export interface CustomStepperColors {
  primary: string;
  lightPrimary: string;
}

@Component({
  selector: 'mojo-stepper',
  templateUrl: './stepper.component.html',
  styleUrls: ['./stepper.component.scss'],
  providers: [{ provide: CdkStepper, useExisting: StepperComponent }],
})
export class StepperComponent
  extends CdkStepper
  implements OnChanges, AfterContentInit {
  @Input() set activeValue(value: string) {
    this.localActiveValue = value;
  }
  @Input() loadingStep: number = null;
  @Input() originalStepList: Step<any>[];
  @Input() singleAdvancement = false;
  @Input() forwardOnly = false;
  /** divider width in rems */
  @Input() dividerWidth = 1;
  /** divider width in rems */
  @Input() dividerWidthOnHover = 0.3;
  /** enable dividers resize on hover */
  @Input() enableDividersResize: boolean;
  @Input() showInactiveLabel = true;
  @Input() showInactiveTooltip = false;
  /**
   * color in hex that overwrites primary color
   * use only for dynamic change, for regular styling use .scss files
   */
  @Input() overwriteColors: CustomStepperColors;
  @Input() disableStepper: boolean;
  @Output() stepSelection = new EventEmitter();

  localActiveValue: string;
  activeIndex: number;
  loadingSteps = {};
  customSteps = {};
  isHovering: boolean;
  customActiveStepStyles = {};

  onClick(step, index) {
    this.isHovering = false;
    this.stepSelection.emit({ step, index });
  }

  isClickable(index: number) {
    return (
      this.singleAdvancementCheck(index, this.activeIndex) &&
      this.forwardOnlyCheck(index, this.activeIndex) &&
      (typeof this.originalStepList[index].isClickable !== 'undefined'
        ? this.originalStepList[index].isClickable
        : true)
    );
  }

  ngAfterContentInit(): void {
    super.ngAfterContentInit();
    this.updateActiveIndex();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.loadingStep && changes.loadingStep.currentValue !== null) {
      this.loadingSteps[changes.loadingStep.currentValue] = true;
    } else {
      const steps = Object.keys(this.loadingSteps);
      steps.forEach((step, i) => (this.loadingSteps[step] = false));
    }
    this.updateActiveIndex();
    this.getCustomSteps(this.originalStepList);
    this.customActiveStepStyles = this.transformColorsIntoNgStylesObject(
      this.overwriteColors,
    );
  }

  hovering(isHovering: boolean) {
    this.isHovering = isHovering && this.enableDividersResize;
  }

  private updateActiveIndex() {
    if (!this.originalStepList) {
      return;
    }

    this.activeIndex = this.originalStepList.findIndex(
      ({ value }) => this.localActiveValue === value,
    );
  }

  private getCustomSteps(steps: Step<any>[]) {
    if (!Array.isArray(steps)) {
      return;
    }
    this.customSteps = {};
    steps.forEach((item, i) => {
      if (item.customTemplate) {
        this.customSteps[i] = true;
      }
    });
  }

  private singleAdvancementCheck(index: number, activeIndex: number) {
    return this.singleAdvancement ? index <= activeIndex + 1 : true;
  }

  private forwardOnlyCheck(itemIndex: number, activeIndex: number) {
    return this.forwardOnly ? itemIndex > activeIndex : true;
  }

  private transformColorsIntoNgStylesObject(colors: CustomStepperColors) {
    if (!colors) {
      return {
        backgroundColor: undefined,
        color: undefined,
      };
    }
    return {
      backgroundColor: colors.lightPrimary,
      color: colors.primary,
    };
  }
}
